/**
 * Admin › Lookups — DESIGN.md §6.6, §6.7 (R10, R13).
 *
 * The screen that makes §1.2's "Lookups are data, not code" literally true:
 * the dev cycle, the tag vocabulary, and the custom fields are all editable
 * here without a deploy.
 *
 * Two rules from the design shape the interactions:
 *  - Statuses are **archivable, never deletable** once used, and archiving
 *    requires choosing where the open cards go (§6.6).
 *  - Custom-field option ids are never reused, so renaming an option keeps
 *    every card that references it (§6.7).
 */
import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Archive, GripVertical, Plus, Star } from 'lucide-react';
import {
  FIELD_TYPES,
  STATUS_CATEGORIES,
  type FieldDefinition,
  type FieldType,
  type Status,
  type StatusCategory,
  type Tag,
} from '@slate/shared';
import { Button } from '@/components/ui/button';
import { ColorDot, Combobox } from '@/components/ui/combobox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { LoadingRegion } from '@/components/ui/spinner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useLookups } from '@/features/lookups/api';
import {
  statusUsage,
  useFieldMutations,
  useStatusMutations,
  useTagMutations,
} from '@/features/lookups/mutations';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/admin/lookups')({
  component: LookupsPage,
});

const CATEGORY_LABELS: Record<StatusCategory, string> = {
  todo: 'To do',
  in_progress: 'In progress',
  done: 'Done',
};

function LookupsPage(): JSX.Element {
  const { data: lookups, isLoading } = useLookups();

  if (isLoading || !lookups) return <LoadingRegion label="Loading lookups" />;

  return (
    <div className="space-y-8">
      <StatusSection statuses={lookups.statuses} />
      <TagSection tags={lookups.tags} />
      <FieldSection fields={lookups.fields} />
    </div>
  );
}

// ---------------------------------------------------------------- statuses

function StatusSection({ statuses }: { statuses: Status[] }): JSX.Element {
  const mutations = useStatusMutations();
  const [adding, setAdding] = useState(false);
  const [archiving, setArchiving] = useState<Status | null>(null);
  const active = statuses.filter((status) => !status.archivedAt);

  return (
    <section>
      <header className="mb-2 flex items-start justify-between gap-2">
        <div>
          <h2 className="font-semibold">Statuses — the dev cycle</h2>
          <p className="text-muted-foreground text-sm">
            Board order, top to bottom. <strong>Category</strong> is what reporting rolls up on, so
            renaming a status never breaks a metric.
          </p>
        </div>
        <Button size="sm" onClick={() => setAdding(true)}>
          <Plus className="size-4" /> Add status
        </Button>
      </header>

      <ul className="divide-y rounded-lg border">
        {statuses.map((status) => (
          <li key={status.id} className="flex flex-wrap items-center gap-2 px-3 py-2">
            <GripVertical className="text-muted-foreground/40 size-4 shrink-0" aria-hidden="true" />
            <span
              className="size-3 shrink-0 rounded-full"
              style={{ backgroundColor: status.color }}
              aria-hidden="true"
            />
            <Input
              className="h-8 w-48"
              aria-label={`Name of ${status.name}`}
              defaultValue={status.name}
              disabled={Boolean(status.archivedAt)}
              onBlur={(event) => {
                const name = event.target.value.trim();
                if (name && name !== status.name) mutations.update.mutate({ id: status.id, name });
              }}
            />
            <Combobox
              className="w-40"
              label={`Category of ${status.name}`}
              value={status.category}
              disabled={Boolean(status.archivedAt)}
              options={STATUS_CATEGORIES.map((category) => ({
                value: category,
                label: CATEGORY_LABELS[category],
              }))}
              onChange={(value) =>
                mutations.update.mutate({ id: status.id, category: value as StatusCategory })
              }
            />
            <input
              type="color"
              className="size-8 shrink-0 cursor-pointer rounded border bg-transparent"
              aria-label={`Colour of ${status.name}`}
              value={status.color}
              disabled={Boolean(status.archivedAt)}
              onChange={(event) =>
                mutations.update.mutate({ id: status.id, color: event.target.value })
              }
            />

            <div className="ml-auto flex items-center gap-1.5">
              {status.archivedAt ? (
                <Badge variant="secondary">Archived</Badge>
              ) : status.isDefault ? (
                <Badge variant="success" title="New cards start here">
                  <Star className="size-3 fill-current" /> Default
                </Badge>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  title="New cards will start in this status"
                  onClick={() => mutations.update.mutate({ id: status.id, isDefault: true })}
                >
                  Make default
                </Button>
              )}
              {!status.archivedAt && !status.isDefault ? (
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7"
                  aria-label={`Archive ${status.name}`}
                  title="Archive — statuses are never deleted, history depends on them"
                  onClick={() => setArchiving(status)}
                >
                  <Archive className="size-3.5" />
                </Button>
              ) : null}
            </div>
          </li>
        ))}
      </ul>

      <AddStatusDialog
        open={adding}
        onOpenChange={setAdding}
        onCreate={(input) => mutations.create.mutate(input)}
      />
      <ArchiveStatusDialog
        status={archiving}
        candidates={active.filter((candidate) => candidate.id !== archiving?.id)}
        onClose={() => setArchiving(null)}
        onArchive={(id, migrateToId) => {
          mutations.archive.mutate({ id, migrateToId });
          setArchiving(null);
        }}
      />
    </section>
  );
}

function AddStatusDialog({
  open,
  onOpenChange,
  onCreate,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreate: (input: {
    name: string;
    color: string;
    category: StatusCategory;
    isDefault: boolean;
  }) => void;
}): JSX.Element {
  const [name, setName] = useState('');
  const [color, setColor] = useState('#64748b');
  const [category, setCategory] = useState<StatusCategory>('in_progress');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add a status</DialogTitle>
          <DialogDescription>
            It joins the end of the board. Category drives reporting, not the name.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="status-name">Name</Label>
            <Input
              id="status-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Awaiting Deploy"
            />
          </div>
          <div className="flex gap-3">
            <div className="flex-1 space-y-1.5">
              <Label htmlFor="status-category">Category</Label>
              <Combobox
                id="status-category"
                label="Category"
                className="h-9"
                value={category}
                options={STATUS_CATEGORIES.map((value) => ({
                  value,
                  label: CATEGORY_LABELS[value],
                }))}
                onChange={(value) => setCategory(value as StatusCategory)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="status-color">Colour</Label>
              <input
                id="status-color"
                type="color"
                className="h-9 w-16 cursor-pointer rounded border bg-transparent"
                value={color}
                onChange={(event) => setColor(event.target.value)}
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={name.trim() === ''}
            onClick={() => {
              onCreate({ name: name.trim(), color, category, isDefault: false });
              setName('');
              onOpenChange(false);
            }}
          >
            Add status
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * §6.6 — "Archiving requires choosing a migration target for open cards." The
 * count is fetched when the dialog opens so the admin sees the blast radius
 * before choosing, not after.
 */
function ArchiveStatusDialog({
  status,
  candidates,
  onClose,
  onArchive,
}: {
  status: Status | null;
  candidates: Status[];
  onClose: () => void;
  onArchive: (id: string, migrateToId: string) => void;
}): JSX.Element {
  const [migrateToId, setMigrateToId] = useState('');
  const [count, setCount] = useState<number | null>(null);

  return (
    <Dialog
      open={status !== null}
      onOpenChange={(open) => {
        if (open) return;
        setMigrateToId('');
        setCount(null);
        onClose();
      }}
    >
      <DialogContent
        onOpenAutoFocus={() => {
          if (status) void statusUsage(status.id).then(setCount);
        }}
      >
        <DialogHeader>
          <DialogTitle>Archive {status?.name}</DialogTitle>
          <DialogDescription>
            Statuses are archived, never deleted — history and reporting depend on them. Open cards
            need somewhere to go.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm">
            {count === null
              ? 'Counting open cards…'
              : count === 0
                ? 'No open cards are in this status.'
                : `${count} open ${count === 1 ? 'card is' : 'cards are'} in this status and will be moved.`}
          </p>
          <div className="space-y-1.5">
            <Label htmlFor="migrate-to">Move open cards to</Label>
            <Combobox
              id="migrate-to"
              label="Move open cards to"
              className="h-9"
              value={migrateToId}
              placeholder="Choose a status…"
              options={candidates.map((candidate) => ({
                value: candidate.id,
                label: candidate.name,
                keywords: [candidate.category],
                icon: <ColorDot color={candidate.color} />,
              }))}
              onChange={setMigrateToId}
            />
          </div>
          <p className="text-muted-foreground text-xs">
            Each moved card gets its own audited status change, so the move shows in every card
            feed.
          </p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!migrateToId || !status}
            onClick={() => status && onArchive(status.id, migrateToId)}
          >
            Archive and migrate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -------------------------------------------------------------------- tags

function TagSection({ tags }: { tags: Tag[] }): JSX.Element {
  const mutations = useTagMutations();
  const [name, setName] = useState('');
  const [color, setColor] = useState('#8b5cf6');

  return (
    <section>
      <header className="mb-2">
        <h2 className="font-semibold">Tags</h2>
        <p className="text-muted-foreground text-sm">
          Flat labels, many per card. Fast filtering — “regression”, “customer-reported”,
          “tech-debt”.
        </p>
      </header>

      <div className="rounded-lg border">
        <ul className="flex flex-wrap gap-2 p-3">
          {tags.length === 0 ? (
            <li className="text-muted-foreground text-sm">No tags yet.</li>
          ) : (
            tags.map((tag) => (
              <li
                key={tag.id}
                className={cn(
                  'flex items-center gap-1.5 rounded-full border px-2 py-1',
                  tag.archivedAt && 'opacity-50',
                )}
              >
                <input
                  type="color"
                  className="size-4 shrink-0 cursor-pointer rounded border-0 bg-transparent p-0"
                  aria-label={`Colour of ${tag.name}`}
                  value={tag.color}
                  disabled={Boolean(tag.archivedAt)}
                  onChange={(event) =>
                    mutations.update.mutate({ id: tag.id, color: event.target.value })
                  }
                />
                <input
                  className="w-28 bg-transparent text-xs outline-none"
                  aria-label={`Name of ${tag.name}`}
                  defaultValue={tag.name}
                  disabled={Boolean(tag.archivedAt)}
                  onBlur={(event) => {
                    const next = event.target.value.trim();
                    if (next && next !== tag.name)
                      mutations.update.mutate({ id: tag.id, name: next });
                  }}
                />
                {tag.archivedAt ? (
                  <span className="text-muted-foreground text-[0.65rem]">archived</span>
                ) : (
                  <button
                    type="button"
                    aria-label={`Archive ${tag.name}`}
                    title="Archive"
                    className="text-muted-foreground hover:text-foreground"
                    onClick={() => mutations.archive.mutate(tag.id)}
                  >
                    <Archive className="size-3" />
                  </button>
                )}
              </li>
            ))
          )}
        </ul>

        <div className="flex gap-2 border-t p-3">
          <input
            type="color"
            className="size-9 shrink-0 cursor-pointer rounded border bg-transparent"
            aria-label="New tag colour"
            value={color}
            onChange={(event) => setColor(event.target.value)}
          />
          <Input
            className="max-w-xs"
            aria-label="New tag name"
            placeholder="New tag…"
            value={name}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' || name.trim() === '') return;
              mutations.create.mutate({ name: name.trim(), color });
              setName('');
            }}
          />
          <Button
            variant="secondary"
            disabled={name.trim() === ''}
            onClick={() => {
              mutations.create.mutate({ name: name.trim(), color });
              setName('');
            }}
          >
            Add tag
          </Button>
        </div>
      </div>
    </section>
  );
}

// ------------------------------------------------------------ custom fields

const TYPE_LABELS: Record<FieldType, string> = {
  text: 'Text',
  number: 'Number',
  date: 'Date',
  checkbox: 'Checkbox',
  select: 'Select',
  multi_select: 'Multi-select',
  /* #85 — "People" rather than "Multi-user": the admin picking this is naming
     a Tester column, not choosing a data type. */
  multi_user: 'People (several)',
  user: 'Person',
  url: 'URL',
};

function FieldSection({ fields }: { fields: FieldDefinition[] }): JSX.Element {
  const mutations = useFieldMutations();
  const [adding, setAdding] = useState(false);

  return (
    <section>
      <header className="mb-2 flex items-start justify-between gap-2">
        <div>
          <h2 className="font-semibold">Custom fields</h2>
          <p className="text-muted-foreground text-sm">
            Typed, filterable, reportable. Select options keep their identity when renamed, so cards
            holding them keep resolving.
          </p>
        </div>
        <Button size="sm" onClick={() => setAdding(true)}>
          <Plus className="size-4" /> Add field
        </Button>
      </header>

      <ul className="divide-y rounded-lg border">
        {fields.length === 0 ? (
          <li className="text-muted-foreground px-3 py-4 text-sm">No custom fields yet.</li>
        ) : (
          fields.map((field) => (
            <li key={field.id} className={cn('px-3 py-2', field.archivedAt && 'opacity-50')}>
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  className="h-8 w-48"
                  aria-label={`Name of ${field.name}`}
                  defaultValue={field.name}
                  disabled={Boolean(field.archivedAt)}
                  onBlur={(event) => {
                    const next = event.target.value.trim();
                    if (next && next !== field.name) {
                      mutations.update.mutate({ id: field.id, name: next });
                    }
                  }}
                />
                <Badge variant="secondary">{TYPE_LABELS[field.type]}</Badge>
                {field.archivedAt ? (
                  <Badge variant="secondary">Archived</Badge>
                ) : (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="ml-auto size-7"
                    aria-label={`Archive ${field.name}`}
                    onClick={() => mutations.archive.mutate(field.id)}
                  >
                    <Archive className="size-3.5" />
                  </Button>
                )}
              </div>

              {field.options && field.options.length > 0 ? (
                <ul className="mt-1.5 flex flex-wrap gap-1.5 pl-1">
                  {field.options.map((option) => (
                    <li
                      key={option.id}
                      className="rounded px-1.5 py-0.5 text-[0.7rem] font-medium"
                      style={{ backgroundColor: `${option.color}1f`, color: option.color }}
                    >
                      {option.label}
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))
        )}
      </ul>

      <AddFieldDialog
        open={adding}
        onOpenChange={setAdding}
        onCreate={(input) => mutations.create.mutate(input)}
      />
    </section>
  );
}

function AddFieldDialog({
  open,
  onOpenChange,
  onCreate,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreate: (input: {
    name: string;
    type: FieldType;
    options?: { label: string; color: string }[];
    scope: 'global';
  }) => void;
}): JSX.Element {
  const [name, setName] = useState('');
  const [type, setType] = useState<FieldType>('text');
  const [optionText, setOptionText] = useState('');
  const needsOptions = type === 'select' || type === 'multi_select';

  const options = optionText
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((label, index) => ({ label, color: OPTION_PALETTE[index % OPTION_PALETTE.length]! }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add a custom field</DialogTitle>
          <DialogDescription>
            Available on every card, and usable as a filter, a sort, and a kanban column source.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="field-name">Name</Label>
            <Input
              id="field-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Release Train"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="field-type">Type</Label>
            <Combobox
              id="field-type"
              label="Type"
              className="h-9"
              value={type}
              options={FIELD_TYPES.map((value) => ({ value, label: TYPE_LABELS[value] }))}
              onChange={(value) => setType(value as FieldType)}
            />
          </div>
          {needsOptions ? (
            <div className="space-y-1.5">
              <Label htmlFor="field-options">Options, one per line</Label>
              <textarea
                id="field-options"
                rows={4}
                className="border-input bg-background w-full rounded-md border p-2 text-sm"
                value={optionText}
                onChange={(event) => setOptionText(event.target.value)}
                placeholder={'Not Required\nRequired\nUpdated'}
              />
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={name.trim() === '' || (needsOptions && options.length === 0)}
            onClick={() => {
              onCreate({
                name: name.trim(),
                type,
                scope: 'global',
                ...(needsOptions ? { options } : {}),
              });
              setName('');
              setOptionText('');
              onOpenChange(false);
            }}
          >
            Add field
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Distinct, legible in both themes — the same family the seed data uses. */
const OPTION_PALETTE = ['#6b7280', '#f59e0b', '#22c55e', '#3b82f6', '#ef4444', '#8b5cf6'];
